import React, { useEffect, useState } from 'react';
import { Dialog, Grid, IconButton, useMediaQuery, useTheme, withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { Settings, Close } from '@material-ui/icons';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Toastr } from 'data/common';
import Field from './Field';
import { inelioSetParamAction, inelioGetAllParamsAction } from '../../../../../../../data/Products/actions';
import Translator from '../../../../../../../../../../../translator';

const initialFields = {
    T_Prod: { name: 'T_Prod', value: 0, range: [-20, 65], unit: '°C', label: 'Production target temperature' },
    T_Hyst_P: { name: 'T_Hyst_P', value: 0, range: [0.1, 10], unit: '°C', label: 'Production hysteresis temperature' },
    TP_Prod: { name: 'TP_Prod', value: 0, range: [0, 600], unit: 'min', label: 'Production duration' },
    S_Cir_C: { name: 'S_Cir_C', value: 0, range: [0, 100], unit: '%', label: 'Hot water circulator speed' },
    T_ReservoirMin: { name: 'T_ReservoirMin', value: 0, range: [-20, 65], unit: '°C', label: 'Min tank temp before closing NH3 in prod' },
    T_Max: { name: 'T_Max', value: 0, range: [50, 350], unit: '°C', label: 'Maximum operating temperature' },
    T_Max_Ambiant: { name: 'T_Max_Ambiant', value: 0, range: [25, 70], unit: '°C', label: 'Maximum operating ambiant temperature' },
    T_ReservoirMax: { name: 'T_ReservoirMax', value: 0, range: [25, 110], unit: '°C', label: 'Maximum tank temperature' },
    P_Ech: { name: 'P_Ech', value: 0, range: [10, 600], unit: 'sec', label: 'Measurement data transmission period' },
    T_Rech: { name: 'T_Rech', value: 0, range: [0, 450], unit: '°C', label: 'Charge target cartridge temperature' },
    T_Reacteur: { name: 'T_Reacteur', value: 0, range: [0, 150], unit: '°C', label: 'Tank temperature during charge cycle' },
    T_Hyst_R: { name: 'T_Hyst_R', value: 0, range: [0.1, 10], unit: '°C', label: 'Hysteresis temperature for charge control' },
    T_EV: { name: 'T_EV', value: 0, range: [25, 100], unit: '°C', label: 'NH3 Solenoid activation temperature' },
    Tp_Rech: { name: 'Tp_Rech', value: 0, range: [0, 600], unit: 'min', label: 'Charging cycle' },
    Tp_Refr: { name: 'Tp_Refr', value: 0, range: [0, 120], unit: 'min', label: 'Cooldown duration' },
    Power: { name: 'Power', value: 0, range: [0, 100], unit: '%', label: 'Stage maximum charging power' },
    E_Prod_Temp: {
        name: 'E_Prod_Temp',
        value: 0,
        range: [0, 2],
        unit: 'enum',
        label: 'Production temperature sensor selection',
        options: [{ label: 'Reservoir/Reacteur', value: 0 }, { label: 'Amb', value: 1 }, { label: 'H20_OUT', value: 2 }],
    },
};

function SettingsModal({ onClose, product_identifier, code, classes, inelioGetAllParamsAction, inelioSetParamAction, toggleLoading, fheCenter_mac }) {
    const [init, setInit] = useState(false);
    const [fields, setFields] = useState(initialFields);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        try {
            toggleLoading(true);
            const respFields = await inelioGetAllParamsAction({ product_identifier, code, fheCenter_mac });
            const newFields = updateFieldsValues(respFields);
            setFields(newFields);
            setInit(true);
        } catch (e) {
            Toastr.error(Translator.translate('socket.error.defaultError'), e);
        } finally {
            toggleLoading(false);
        }
    };

    const updateFieldsValues = respFields => {
        return Object.entries(respFields).reduce((acc, [field, val]) => {
            if (initialFields[field]) {
                initialFields[field].value = val;
            }
            return acc;
        }, initialFields);
    };

    if (!init) return null;

    return (
        <Dialog open onClose={onClose} maxWidth={false} fullScreen={fullScreen}>
            <Grid container className={classes.container}>
                <Grid container item xs={12}>
                    <Grid className={classes.title} container alignContent="center" item xs={11}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Settings className={classes.icon} />
                        </div>
                        {Translator.translate('general.settings')}
                    </Grid>
                    <Grid container alignItems="center" justify="center" item xs={1}>
                        <IconButton aria-label="close" className={classes.closeIcon} size="medium" onClick={onClose}>
                            <Close fontSize="inherit" />
                        </IconButton>
                    </Grid>
                </Grid>
                <Grid container>
                    {Object.entries(fields).map(([key, value]) => (
                        <Field
                            fheCenter_mac={fheCenter_mac}
                            key={key}
                            field={value}
                            inelioSetParamAction={inelioSetParamAction}
                            product_identifier={product_identifier}
                            code={code}
                        />
                    ))}
                </Grid>
            </Grid>
        </Dialog>
    );
}

const styles = theme => ({
    container: {
        maxWidth: 1200,
        padding: 18,
    },
    item: {
        padding: 10,
    },
    saveIcon: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    title: {
        color: theme.palette.primary.main,
        fontSize: 34,
        fontWeight: 400,
        marginBottom: 12,
    },
    icon: {
        fontSize: '2rem',
        marginRight: 8,
    },
});

SettingsModal.propTypes = {
    fheCenter_mac: PropTypes.string.isRequired,
    inelioGetAllParamsAction: PropTypes.func.isRequired,
    inelioSetParamAction: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    toggleLoading: PropTypes.func.isRequired,
    product_identifier: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
};
SettingsModal.defaultProps = {};

export default withRouter(
    connect(
        null,
        dispatch => bindActionCreators({ inelioSetParamAction, inelioGetAllParamsAction }, dispatch),
    )(withStyles(styles, { withTheme: true })(SettingsModal)),
);



// WEBPACK FOOTER //
// ./environments/genericEnv/scenes/Products/components/Product/Fhe/SDM19INL/components/SettingsModal.js